import React from "react";
import { AlertTriangle, ArrowRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePlan } from "../../contexts/PlanContext";
import "./dashboard.scss";

/**
 * GracePeriodBanner — Shown on the dashboard when the institute's trial or
 * paid subscription has expired but they're still within the 7-day grace window.
 *
 * Also shown when the trial is active but about to expire (≤3 days).
 */
const GracePeriodBanner = () => {
  const navigate = useNavigate();
  const { trial, grace } = usePlan();

  // ── Grace period (trial or subscription expired, within 7-day window) ──
  if (grace?.inGrace) {
    const isTrialGrace = grace.status === "trial_expired";
    const daysLeft = grace.daysLeft ?? 0;

    return (
      <div className="db-grace-banner db-grace-banner--danger">
        <div className="db-grace-banner__icon">
          <AlertTriangle size={20} />
        </div>
        <div className="db-grace-banner__content">
          <div className="db-grace-banner__title">
            {isTrialGrace ? "Your free trial has expired" : "Your subscription has expired"}
          </div>
          <div className="db-grace-banner__text">
            You are on a <strong>7-day grace period</strong>.{" "}
            {daysLeft > 0
              ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining before your account is downgraded to the free plan.`
              : "Your account will be downgraded today."}
            {" "}Upgrade now to keep all your features and data limits.
          </div>
        </div>
        <button className="db-grace-banner__btn" onClick={() => navigate("/billing")}>
          Upgrade Now <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  // ── Trial expiring soon (≤3 days) ──
  if (trial?.active && trial.daysLeft !== null && trial.daysLeft <= 3) {
    return (
      <div className="db-grace-banner db-grace-banner--warning">
        <div className="db-grace-banner__icon">
          <Clock size={20} />
        </div>
        <div className="db-grace-banner__content">
          <div className="db-grace-banner__title">
            {trial.daysLeft === 0
              ? "Your trial expires today!"
              : `Your trial expires in ${trial.daysLeft} day${trial.daysLeft !== 1 ? "s" : ""}`}
          </div>
          <div className="db-grace-banner__text">
            After the trial ends, you'll have a 7-day grace period before being moved to the free plan.
            Upgrade now to avoid any interruption.
          </div>
        </div>
        <button className="db-grace-banner__btn" onClick={() => navigate("/billing")}>
          Upgrade Now <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return null;
};

export default GracePeriodBanner;
